import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, LogOut, Plus, Search, PanelLeftOpen, Trash2, X, Loader } from 'lucide-react';
import ChatMessage from './message/ChatMessage';
import MessageInput from './layout/MessageInput';
import TypingIndicator from './common/TypingIndicator';
import { analyticsAPI } from '../utils/api';

const mapVisualizationTypeToMessageType = (apiData) => {
  const results = Array.isArray(apiData.results) ? apiData.results : [];

  // Single-row results are always shown as a detail card — never as a chart
  if (results.length === 1) return 'detail';

  const vizType = apiData.visualizationType || apiData.type;
  if (vizType === 'metric' || vizType === 'card') return 'cards';
  if (vizType === 'line' || vizType === 'line_chart') return 'line-chart';
  if (vizType === 'bar' || vizType === 'bar_chart') return 'bar-chart';
  if (vizType === 'pie' || vizType === 'pie_chart') return 'pie-chart';
  if (vizType === 'donut' || vizType === 'donut_chart') return 'donut-chart';
  if (vizType === 'area' || vizType === 'area_chart') return 'area-chart';
  if (vizType === 'table') return 'table';
  if (results.length > 0) return 'table';
  return 'text';
};

const buildAssistantMessage = (payload, idSeed = Date.now()) => ({
  id: idSeed,
  type: mapVisualizationTypeToMessageType(payload),
  content: payload.results || [],
  isUser: false,
  apiData: {
    results: payload.results || [],
    metadata: payload.metadata || {},
    insights: payload.insights || [],
    sql: payload.sql || '',
    query: payload.query || '',
    visualizationType: payload.visualizationType || payload.type,
    type: payload.type,
    warnings: payload.warnings || [],
    debug: payload.debug,
    success: payload.success,
  },
});

const AnalyticsChatUI = ({ onLogout }) => {
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState('');
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [retryStatus, setRetryStatus] = useState({ isRetrying: false, attemptNumber: 0 });
  const [sessionFilter, setSessionFilter] = useState('');
  const [showSidebar, setShowSidebar] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [modelProvider] = useState('claude');
  const [editingMessage, setEditingMessage] = useState(null);
  const [sessionPendingDelete, setSessionPendingDelete] = useState(null);
  const [toast, setToast] = useState(null);
  const [uploadState, setUploadState] = useState('idle'); // idle | uploading | success
  const [uploadMsg, setUploadMsg] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [deletingFile, setDeletingFile] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // null | fileName string
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const retryTimeoutRef = useRef(null);
  const retryIntervalRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const toastTimerRef = useRef(null);

  const showToast = (message, type = 'error') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, type });
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploadState('uploading');
    setUploadProgress(0);
    setUploadMsg(`Uploading ${file.name}…`);

    const result = await analyticsAPI.uploadExcel(file, (progressEvent) => {
      const pct = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
      setUploadProgress(pct);
    });
    if (result.success) {
      const rows = result.data?.totalRows ?? '?';
      setUploadState('success');
      setUploadMsg(`✓ ${file.name} — ${rows} rows loaded`);
      showToast(`${file.name} uploaded successfully (${rows} rows)`, 'success');
      void loadUploadedFiles();
      setTimeout(() => { setUploadState('idle'); setUploadMsg(''); setUploadProgress(0); }, 4000);
    } else {
      setUploadState('idle');
      setUploadMsg('');
      setUploadProgress(0);
      showToast(result.error || 'Upload failed', 'error');
    }
  };

  const loadUploadedFiles = async () => {
    setLoadingFiles(true);
    const result = await analyticsAPI.listUploadedFiles();
    if (result.success) {
      setUploadedFiles(Array.isArray(result.data?.files) ? result.data.files : []);
    } else {
      showToast(result.error || 'Failed to load uploaded files', 'error');
    }
    setLoadingFiles(false);
  };

  const handleDeleteUploadedFile = async (fileName) => {
    if (!fileName || deletingFile) return;
    setConfirmDelete(null);
    setDeletingFile(fileName);
    const result = await analyticsAPI.deleteUploadedFile(fileName);
    if (result.success) {
      showToast(`Deleted ${fileName}`, 'success');
      await loadUploadedFiles();
    } else {
      showToast(result.error || `Failed to delete ${fileName}`, 'error');
    }
    setDeletingFile('');
  };

  const checkScrollPosition = () => {
    if (!messagesContainerRef.current) return;
    
    const container = messagesContainerRef.current;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    
    // Show "scroll to bottom" button if user is scrolled up more than 300px
    setShowScrollToBottom(distanceFromBottom > 300);
  };

  const scrollToBottom = (force = false) => {
    if (!messagesContainerRef.current) return;
    
    const container = messagesContainerRef.current;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    
    // Only auto-scroll if user is near bottom (within 200px) or force is true
    if (force || distanceFromBottom < 200) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setShowScrollToBottom(false);
    }
  };

  useEffect(() => {
    scrollToBottom();
    checkScrollPosition();
  }, [messages]);

  useEffect(() => {
    const mobile = window.innerWidth < 1024;
    setIsMobile(mobile);
    setShowSidebar(!mobile);

    const onResize = () => {
      const nowMobile = window.innerWidth < 1024;
      setIsMobile(nowMobile);
      // Only auto-close sidebar when crossing into mobile for the first time
      if (nowMobile && window.innerWidth < 1024 && !isMobile) {
        setShowSidebar(false);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const getConversationHistory = () =>
    messages
      .filter((msg) => msg.type !== 'suggestions' && msg.type !== 'followup-suggestions')
      .map((msg) => {
        if (msg.isUser) {
          return { role: 'user', content: msg.content, timestamp: new Date(msg.id).toISOString() };
        }
        return {
          role: 'assistant',
          content: msg.apiData?.query || msg.content || 'Assistant response',
          timestamp: new Date(msg.id).toISOString(),
          sql: msg.apiData?.sql,
        };
      });

  const hydrateMessages = (sessionData) => {
    const hydrated = (sessionData?.messages || []).map((item, index) => {
      if (item.role === 'user') {
        return {
          id: Date.now() + index,
          type: 'text',
          content: item.content,
          isUser: true,
          messageIndex: index,
        };
      }
      if (item.apiResponse) {
        return {
          ...buildAssistantMessage(item.apiResponse, Date.now() + index),
          messageIndex: index,
        };
      }
      return {
        id: Date.now() + index,
        type: 'text',
        content: item.content || item.error || 'Response generated.',
        isUser: false,
        messageIndex: index,
      };
    });

    return hydrated.length > 0
      ? hydrated
      : [
          {
            id: 1,
            type: 'text',
            content:
              "Ask anything about your data. I'll keep context in this session and save all history.",
            isUser: false,
          },
        ];
  };

  const loadSession = async (sessionId) => {
    const response = await analyticsAPI.getSession(sessionId);
    if (!response.success || !response.data?.success) {
      showToast('Could not load session. Please try again.');
      return;
    }
    setActiveSessionId(sessionId);
    setMessages(hydrateMessages(response.data.session));
    if (isMobile) setShowSidebar(false);
  };

  const createNewSession = async (initialPrompt = '') => {
    const createResponse = await analyticsAPI.createSession(initialPrompt);
    if (!createResponse.success || !createResponse.data?.success) {
      showToast('Could not create a new session. Please try again.');
      return null;
    }
    const newSession = createResponse.data.session;
    setSessions((prev) => [newSession, ...prev]);
    setActiveSessionId(newSession.sessionId);
    setMessages([
      {
        id: 1,
        type: 'text',
        content:
          "New session started. Ask your first question and I'll remember everything in this chat.",
        isUser: false,
      },
    ]);
    return newSession.sessionId;
  };

  useEffect(() => {
    const initialize = async () => {
      setIsLoadingSessions(true);

      const [health, sessionsResponse] = await Promise.all([
        analyticsAPI.healthCheck(),
        analyticsAPI.listSessions(50),
      ]);

      if (!health.success) {
        setMessages([{
          id: 1,
          type: 'text',
          content: 'Cannot connect to backend API right now. Start the Nest server and refresh.',
          isUser: false,
        }]);
        setIsLoadingSessions(false);
        return;
      }

      const loadedSessions =
        sessionsResponse.success && sessionsResponse.data?.success
          ? sessionsResponse.data.sessions
          : [];
      setSessions(loadedSessions);

      if (loadedSessions.length > 0) {
        await loadSession(loadedSessions[0].sessionId);
      } else {
        await createNewSession();
      }

      setIsLoadingSessions(false);
    };

    initialize();
    void loadUploadedFiles();
  }, []);

  const filteredSessions = useMemo(
    () =>
      sessions.filter((session) =>
        session.title?.toLowerCase().includes(sessionFilter.toLowerCase()),
      ),
    [sessions, sessionFilter],
  );

  const handleSuggestionClick = (suggestion) => {
    void handleSend(suggestion);
  };

  const handleDeleteSession = async (sessionId) => {
    const response = await analyticsAPI.deleteSession(sessionId);
    if (!response.success) {
      showToast('Could not delete session. Please try again.');
      return;
    }

    const remaining = sessions.filter((s) => s.sessionId !== sessionId);
    setSessions(remaining);

    if (activeSessionId === sessionId) {
      if (remaining.length > 0) {
        await loadSession(remaining[0].sessionId);
      } else {
        await createNewSession();
      }
    }
  };

  const handleStartEditPrompt = (message) => {
    if (!activeSessionId || typeof message?.messageIndex !== 'number') return;
    setEditingMessage({
      messageIndex: message.messageIndex,
      originalContent: message.content,
    });
    setInputValue(message.content || '');
  };

  const stopRetryTimers = () => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    if (retryIntervalRef.current) {
      clearInterval(retryIntervalRef.current);
      retryIntervalRef.current = null;
    }
  };

  const handleSend = async (customMessage) => {
    const messageText = customMessage || inputValue.trim();
    if (!messageText) return;

    let sessionId = activeSessionId;
    if (!sessionId) {
      const created = await createNewSession(messageText);
      if (!created) return;
      sessionId = created;
    }

    if (editingMessage && typeof editingMessage.messageIndex === 'number') {
      const updateResponse = await analyticsAPI.updateUserMessage(
        sessionId,
        editingMessage.messageIndex,
        messageText,
      );
      if (updateResponse.success) {
        await loadSession(sessionId);
        setEditingMessage(null);
        setInputValue('');
      }
      return;
    }

    const userMessage = {
      id: Date.now(),
      type: 'text',
      content: messageText,
      isUser: true,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    
    // Force scroll to bottom when user sends a message
    setTimeout(() => scrollToBottom(true), 100);
    
    setIsTyping(true);
    setIsLoading(true);
    setRetryStatus({ isRetrying: false, attemptNumber: 0 });

    retryTimeoutRef.current = setTimeout(() => {
      setRetryStatus({ isRetrying: true, attemptNumber: 1 });
      let attempt = 1;
      retryIntervalRef.current = setInterval(() => {
        attempt += 1;
        if (attempt <= 10) {
          setRetryStatus({ isRetrying: true, attemptNumber: attempt });
        } else {
          stopRetryTimers();
        }
      }, 5000);
    }, 10000);

    try {
      const conversationHistory = [
        ...getConversationHistory(),
        { role: 'user', content: messageText, timestamp: new Date().toISOString() },
      ];
      const response = await analyticsAPI.executeQuery(
        messageText,
        sessionId,
        conversationHistory,
        modelProvider,
      );

      if (!response.success || !response.data) {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now() + 1,
            type: 'text',
            content: `Error: ${response.error || 'Failed to process query.'}`,
            isUser: false,
          },
        ]);
        return;
      }

      const apiData = response.data;
      const assistantMessage = buildAssistantMessage(apiData, Date.now() + 1);
      const followupMessage =
        Array.isArray(apiData.followupSuggestions) &&
        apiData.followupSuggestions.length > 0
          ? {
              id: Date.now() + 2,
              type: 'followup-suggestions',
              content: apiData.followupSuggestions,
              isUser: false,
            }
          : null;

      setMessages((prev) =>
        followupMessage ? [...prev, assistantMessage, followupMessage] : [...prev, assistantMessage],
      );

      // Smart scroll: only auto-scroll if user is already near bottom
      setTimeout(() => scrollToBottom(false), 200);

      setSessions((prev) =>
        prev
          .map((s) =>
            s.sessionId === sessionId
              ? {
                  ...s,
                  title: s.title === 'New Chat Session' ? messageText.slice(0, 60) : s.title,
                  messageCount: (s.messageCount || 0) + 2,
                  updatedAt: new Date().toISOString(),
                  lastMessage: messageText,
                }
              : s,
          )
          .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)),
      );
    } finally {
      setIsTyping(false);
      setIsLoading(false);
      setRetryStatus({ isRetrying: false, attemptNumber: 0 });
      stopRetryTimers();
    }
  };

  const activeSession = sessions.find((s) => s.sessionId === activeSessionId);

  return (
    <div className="h-screen bg-slate-100 flex overflow-hidden relative">
      {/* Toast notification */}
      {toast && (
        <div
          className={`absolute top-4 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 px-4 py-3 rounded-xl shadow-xl text-sm font-medium border animate-fade-in-down ${
            toast.type === 'error'
              ? 'bg-rose-50 border-rose-200 text-rose-800'
              : 'bg-emerald-50 border-emerald-200 text-emerald-800'
          }`}
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {toast.message}
          <button onClick={() => setToast(null)} className="ml-2 opacity-60 hover:opacity-100">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {sessionPendingDelete && (
        <div className="absolute inset-0 z-50 flex items-center justify-center px-4">
          <button
            className="absolute inset-0 bg-slate-900/55"
            onClick={() => setSessionPendingDelete(null)}
            aria-label="Close delete confirmation"
          />
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  Delete Chat Session?
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  This action will permanently delete this session and all its messages. It cannot be undone.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setSessionPendingDelete(null)}
                className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await handleDeleteSession(sessionPendingDelete);
                  setSessionPendingDelete(null);
                }}
                className="px-4 py-2 text-sm rounded-lg bg-rose-600 text-white hover:bg-rose-700"
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
      {isMobile && showSidebar && (
        <button
          className="absolute inset-0 bg-slate-900/50 z-30"
          onClick={() => setShowSidebar(false)}
          aria-label="Close sessions panel"
        />
      )}
      <aside
        className={`${
          isMobile
            ? `absolute left-0 top-0 h-full z-40 ${showSidebar ? 'w-[88vw] max-w-[340px]' : 'w-0'}`
            : `${showSidebar ? 'w-80' : 'w-0'}`
        } bg-slate-900 text-slate-100 flex flex-col border-r border-slate-800 transition-all duration-300 overflow-hidden`}
      >
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={() => void createNewSession()}
              className="flex-1 flex items-center justify-center gap-2 rounded-lg border border-slate-700 bg-slate-800 hover:bg-slate-700 px-4 py-3 text-sm font-medium transition"
            >
              <Plus className="w-4 h-4" />
              New Session
            </button>
            {isMobile && (
              <button
                onClick={() => setShowSidebar(false)}
                className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800"
                aria-label="Close sessions panel"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="mt-3 relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <input
              value={sessionFilter}
              onChange={(e) => setSessionFilter(e.target.value)}
              placeholder="Search sessions..."
              className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-slate-500"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          {isLoadingSessions && (
            <p className="text-xs text-slate-400 px-2 py-2">Loading sessions...</p>
          )}
          {!isLoadingSessions && filteredSessions.length === 0 && (
            <p className="text-xs text-slate-400 px-3 py-3 rounded-md bg-slate-800/60">
              No sessions found.
            </p>
          )}
          {!isLoadingSessions &&
            filteredSessions.map((session) => (
              <div
                key={session.sessionId}
                className={`w-full rounded-lg p-2 transition ${
                  activeSessionId === session.sessionId
                    ? 'bg-slate-700 border border-slate-600'
                    : 'bg-slate-800 hover:bg-slate-700'
                }`}
              >
                <div className="flex items-start gap-2">
                  <button
                    onClick={() => void loadSession(session.sessionId)}
                    className="flex-1 text-left px-1"
                  >
                    <p className="text-sm font-medium truncate">{session.title || 'Untitled'}</p>
                    <p className="text-xs text-slate-400 mt-1 truncate">
                      {session.lastMessage || 'No messages yet'}
                    </p>
                  </button>
                  <button
                    onClick={() => setSessionPendingDelete(session.sessionId)}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-400 hover:text-rose-300 hover:bg-slate-600"
                    aria-label="Delete chat session"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0">
        <div className="px-4 md:px-8 py-3 md:py-4 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowSidebar((prev) => !prev)}
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                aria-label="Toggle sessions panel"
              >
                <PanelLeftOpen className="w-4 h-4" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-slate-900">Procurement Agent</h1>
                {activeSession?.title && (
                  <p className="text-xs text-slate-500 truncate max-w-[260px]">
                    {activeSession.title}
                  </p>
                )}
              </div>
            </div>
            <div className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50">
              <span className="text-xs font-medium text-slate-600">
                Uploaded Files ({uploadedFiles.length})
              </span>
              {loadingFiles ? (
                <Loader className="w-3 h-3 animate-spin text-slate-500" />
              ) : (
                <button
                  onClick={() => void loadUploadedFiles()}
                  className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                  type="button"
                >
                  Refresh
                </button>
              )}
            </div>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-50 text-sm font-medium"
              aria-label="Logout"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>

        <div 
          ref={messagesContainerRef} 
          className="flex-1 overflow-y-auto px-3 sm:px-4 md:px-8 py-4 md:py-6 relative"
          onScroll={checkScrollPosition}
        >
          <div className="max-w-5xl mx-auto space-y-6">
            {uploadedFiles.length > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-sm font-semibold text-slate-700">Uploaded files</h2>
                  <span className="text-xs text-slate-500">{uploadedFiles.length} file(s)</span>
                </div>
                <div className="space-y-2">
                  {uploadedFiles.map((file) => (
                    <div
                      key={file.name}
                      className="flex items-center justify-between bg-white border border-slate-200 rounded-lg px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="text-sm text-slate-800 truncate">{file.name}</p>
                        <p className="text-xs text-slate-500">
                          {(Number(file.size || 0) / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <button
                        onClick={() => setConfirmDelete(file.name)}
                        disabled={!!deletingFile}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
                        type="button"
                      >
                        {deletingFile === file.name ? (
                          <Loader className="w-3 h-3 animate-spin" />
                        ) : (
                          <Trash2 className="w-3 h-3" />
                        )}
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                message={message}
                isUser={message.isUser}
                onSuggestionClick={handleSuggestionClick}
                onEditUserMessage={handleStartEditPrompt}
                isQueryLoading={isLoading}
              />
            ))}
            {(isTyping || isLoading) && (
              <TypingIndicator
                showRetryMessage={retryStatus.isRetrying}
                retryAttempt={retryStatus.attemptNumber}
              />
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Scroll to bottom button - appears when user scrolls up */}
          {showScrollToBottom && (
            <button
              onClick={() => scrollToBottom(true)}
              className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-slate-50 text-slate-700 rounded-full shadow-lg border border-slate-200 text-sm font-medium transition-all hover:shadow-xl"
              aria-label="Scroll to latest message"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
              New message
            </button>
          )}
        </div>

        <MessageInput
          inputValue={inputValue}
          onInputChange={setInputValue}
          onSend={() => void handleSend()}
          onUploadFile={(e) => void handleExcelUpload(e)}
          uploadState={uploadState}
          uploadMsg={uploadMsg}
          uploadProgress={uploadProgress}
          isEditing={!!editingMessage}
          onCancelEdit={() => {
            setEditingMessage(null);
            setInputValue('');
          }}
          disabled={!inputValue.trim() || isLoading}
        />
      </main>

      {/* ── Confirm-delete modal ─────────────────────────────────── */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-rose-100 flex items-center justify-center">
                <Trash2 className="w-4 h-4 text-rose-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Remove file?</h3>
                <p className="text-sm text-gray-500 mt-1 break-all">
                  <span className="font-medium text-gray-700">{confirmDelete}</span> will be
                  permanently removed from the server and its data will no longer be queryable.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={() => void handleDeleteUploadedFile(confirmDelete)}
                className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium"
                type="button"
              >
                Yes, remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsChatUI;

